import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAccess } from '../lib/access';
import AccessInfo from './AccessInfo';

// Door code and WiFi live in property_access_info (see AccessInfo), not on properties.
const FIELDS = [
  { key: 'address', label: 'Address', type: 'text' },
  { key: 'platform', label: 'Platform', type: 'text' },
  { key: 'listing_url', label: 'Listing URL', type: 'url' },
  { key: 'checkin_time', label: 'Check-in Time', type: 'text', placeholder: '4:00 PM' },
  { key: 'checkout_time', label: 'Check-out Time', type: 'text', placeholder: '10:00 AM' },
  { key: 'bedrooms', label: 'Bedrooms', type: 'number' },
  { key: 'bathrooms', label: 'Bathrooms', type: 'number', step: '0.5' },
  { key: 'max_guests', label: 'Max Guests', type: 'number' },
  { key: 'sqft', label: 'Sq Ft', type: 'number' },
];

export default function PropertyInfoEditor({ property, theme, stats, onSaved }) {
  const access = useAccess();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showMasked, setShowMasked] = useState({});

  const startEdit = () => {
    const f = {};
    FIELDS.forEach(field => { f[field.key] = property[field.key] || ''; });
    setForm(f);
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {};
    FIELDS.forEach(field => {
      const val = form[field.key];
      if (field.type === 'number') {
        payload[field.key] = val ? parseFloat(val) : null;
      } else {
        payload[field.key] = val?.toString().trim() || null;
      }
    });

    const { data, error } = await supabase
      .from('properties')
      .update(payload)
      .eq('id', property.id)
      .select('*')
      .single();

    setSaving(false);
    if (!error && data) {
      onSaved(data);
      setEditing(false);
    }
  };

  const toggleMask = (key) => {
    setShowMasked(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const displayValue = (field) => {
    const val = property[field.key];
    if (!val) return '\u2014';
    if (field.masked && !showMasked[field.key]) return '\u2022\u2022\u2022\u2022\u2022\u2022';
    if (field.key === 'sqft') return parseInt(val).toLocaleString();
    return val;
  };

  return (
    <div className="property-info-card">
      <div className="property-info-banner" style={{ background: theme.accent }}>
        <h2>{property.name}</h2>
        <p>{property.address || 'No address set'}</p>
      </div>

      <div className="property-info-body">
        {!editing ? (
          <>
            <div className="property-info-grid">
              {FIELDS.map(field => (
                <div key={field.key} className="property-info-row">
                  <span className="property-info-label">{field.label}</span>
                  <span className="property-info-value">
                    {field.key === 'listing_url' && property[field.key] ? (
                      <a href={property[field.key]} target="_blank" rel="noopener noreferrer">
                        View Listing &rarr;
                      </a>
                    ) : (
                      displayValue(field)
                    )}
                    {field.masked && property[field.key] && (
                      <button className="mask-toggle" onClick={() => toggleMask(field.key)}>
                        {showMasked[field.key] ? 'Hide' : 'Show'}
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>

            <AccessInfo propertyId={property.id} />

            <div className="property-quick-stats">
              <div className="property-stat">
                <span className="property-stat-value" style={{ color: theme.accent }}>{stats.openTasks}</span>
                <span className="property-stat-label">Open Tasks</span>
              </div>
              <div className="property-stat">
                <span className="property-stat-value" style={{ color: theme.accent }}>
                  ${stats.ytdCost.toLocaleString()}
                </span>
                <span className="property-stat-label">YTD Maintenance</span>
              </div>
            </div>

            {access.can(property.id, 'edit') && (
              <button className="btn btn-secondary" onClick={startEdit} style={{ marginTop: 16 }}>
                Edit Details
              </button>
            )}
          </>
        ) : (
          <>
            <div className="property-info-grid">
              {FIELDS.map(field => (
                <div key={field.key} className="property-info-row">
                  <label className="property-info-label">{field.label}</label>
                  <input
                    type={field.type === 'url' ? 'url' : field.type === 'number' ? 'number' : 'text'}
                    step={field.step}
                    value={form[field.key]}
                    onChange={e => setForm({ ...form, [field.key]: e.target.value })}
                    placeholder={field.placeholder || ''}
                    className="property-info-input"
                  />
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button className="btn btn-secondary" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
